import { useEffect, useState } from "react";
import { useMediaQuery } from "@mantine/hooks";
import {
  Button,
  Center,
  CloseButton,
  Drawer,
  Flex,
  Image,
  ScrollArea,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { ArrowRight2 } from "iconsax-react";
import SignUp from "./SignUp";
import SignIn from "./SignIn";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/auth";
import { client_selected } from "../../client";
import TempPassword from "./TempPassword";
import LayoutInfoAuth from "./_components/LayoutInfoAuth";
import { useTranslation } from "react-i18next";
import { LanguageSelect } from "../../components";
import LayoutInfoAuthGenial from "./_components/LayoutInfoAuthGenial";
interface IAuthButtonProps {
  type: "signin" | "singup";
  path: string;
  isActive: boolean;
}

const Authentication = () => {
  const isSpider =
    client_selected.name !== "Empiricus" && client_selected.name !== "Genial";
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { returnPath, cancelRequests, signInWithToken } = useAuth();
  const { t } = useTranslation();
  const isLargerThan991 = useMediaQuery("(min-width: 991px)");
  const logo = client_selected.image.logo2;
  const name = client_selected.name;
  const [isLoading, setIsLoading] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const isAuthPath = location.pathname === "/autenticacao";
  const createAccount = location.pathname.includes("/criar-conta");
  const tempPassword = location.pathname.includes("/mudar-senha-temporaria");
  const verifyEmail = location.pathname.includes("/verficar-email");
  const emailVerified = location.pathname.includes("/email-verificado");
  const expiredLink = location.pathname.includes("/link-de-ativacao-expirou");
  const genialLogin = location.pathname.includes("/genial");
  const isInfo = verifyEmail || emailVerified || expiredLink || genialLogin;

  useEffect(() => {
    if (isAuthPath) {
      navigate("/autenticacao/acessar-conta");
      if (isLargerThan991) {
        setIsDrawerOpen(true);
      }
    }
    // eslint-disable-next-line
  }, [isAuthPath, tempPassword, isLargerThan991]);

  const handleCancelLogin = () => {
    cancelRequests();
    returnPath("/autenticacao/acessar-conta");
  };

  const AuthButton = ({ type, path, isActive }: IAuthButtonProps) => {
    const handleOnClick = () => {
      if (isLargerThan991) {
        navigate(path);
      } else {
        navigate(path);
        setIsDrawerOpen(true);
      }
    };

    const label =
      type === "signin"
        ? t("autenticacao.button_signin.title")
        : t("autenticacao.button_signup.title");
    const description =
      type === "signin"
        ? t("autenticacao.button_signin.description")
        : t("autenticacao.button_signup.description");
    return (
      <Button
        onClick={handleOnClick}
        bg={isActive ? theme.colors.primaryOpacity[1] : ""}
        sx={{
          border: isActive ? `1px solid ${theme.colors.primary[0]}` : "",
        }}
        rightIcon={<ArrowRight2 size={28} />}
        variant="login"
        mb={16}
        h={88}
        disabled={isLoading && !isActive}
      >
        {label}
        {isLargerThan991 ? <span>{description}</span> : null}
      </Button>
    );
  };

  return (
    <Flex
      w="100%"
      h="100%"
      pos={"fixed"}
      align={"center"}
      justify={"center"}
      style={{
        zIndex: 1000,
        background: theme.colors.basic[0],
      }}
    >
      {isInfo ? (
        emailVerified ? (
          <LayoutInfoAuth
            description={t("autenticacao.email_verificado.description")}
            text={t("autenticacao.email_verificado.text")}
            title={t("autenticacao.email_verificado.title")}
          />
        ) : expiredLink ? (
          <LayoutInfoAuth
            description={t("autenticacao.link_de_ativacao_expirou.description")}
            text={t("autenticacao.link_de_ativacao_expirou.text")}
            title={t("autenticacao.link_de_ativacao_expirou.title")}
          />
        ) : genialLogin ? (
          <LayoutInfoAuthGenial />
        ) : (
          <LayoutInfoAuth
            description={t("autenticacao.verficar_email.description")}
            text={t("autenticacao.verficar_email.text")}
            title={t("autenticacao.verficar_email.title")}
          />
        )
      ) : (
        <Flex maw={"1920px"} w={"100%"} h={"100%"}>
          <Flex
            bg={theme.colors.basic[0]}
            direction="column"
            w={{ base: "100%", 991: "auto" }}
            miw={{ base: "auto", 991: 450, 1200: 556 }}
            p={{
              base: "32px 20px",
              550: "80px",
              991: "80px",
              1600: "80px",
            }}
            justify="center"
            sx={{
              boxShadow: "2px 0px 0px rgba(0, 0, 0, 0.15)",
              maxWidth: "1920px",
            }}
          >
            <Flex direction="column">
              <Image
                src={logo}
                height={"auto"}
                width="225px"
                fit="contain"
                alt="logo-trader"
                style={{ cursor: isSpider ? "pointer" : "default" }}
                onClick={() => (isSpider ? handleCancelLogin : null)}
              />
              {isSpider ? (
                <Text
                  w={{ base: 250, 991: 270 }}
                  mt={48}
                  fw={{ base: 400, 991: 500 }}
                  color={theme.colors.basic[5]}
                  fz={{ base: 32, 991: 28 }}
                  lh="155%"
                  align="start"
                >
                  {t("autenticacao.description_1")}
                  <Text
                    fw={{ base: 700, 991: 900 }}
                    sx={{
                      background: theme.colors.primary[1],
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {name}
                  </Text>
                  {t("autenticacao.description_2")}
                </Text>
              ) : (
                <Text
                  w={{ base: 250, 991: 270 }}
                  mt={48}
                  fw={{ base: 400, 991: 500 }}
                  color={theme.colors.basic[5]}
                  fz={{ base: 32, 991: 28 }}
                  lh="155%"
                  align="start"
                >
                  {t("autenticacao.description_empiricus")}
                  <Text
                    fw={{ base: 700, 991: 900 }}
                    sx={{
                      background: theme.colors.primary[1],
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    Empiricus!
                  </Text>
                </Text>
              )}

              <Text color={theme.colors.basic[4]} size={20} mt={12} mb={48}>
                {t("autenticacao.sub_description")}
              </Text>

              <AuthButton
                type="signin"
                path="/autenticacao/acessar-conta"
                isActive={!createAccount}
              />
              {client_selected.autoRegister ? (
                <AuthButton
                  type="singup"
                  path="/autenticacao/criar-conta"
                  isActive={createAccount}
                />
              ) : null}
            </Flex>
            <LanguageSelect />
          </Flex>
          {isLargerThan991 ? (
            <ScrollArea
              w="100%"
              scrollHideDelay={500}
              styles={{
                viewport: {
                  ">div": {
                    height: "100%",
                  },
                },
              }}
            >
              <Center
                w="100%"
                h="100%"
                style={{
                  display: "table-cell",
                  textAlign: "center",
                  verticalAlign: "middle",
                }}
              >
                {tempPassword ? (
                  <TempPassword
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                  />
                ) : createAccount ? (
                  <SignUp isLoading={isLoading} setIsLoading={setIsLoading} />
                ) : (
                  <SignIn isLoading={isLoading} setIsLoading={setIsLoading} />
                )}
              </Center>
            </ScrollArea>
          ) : (
            <Drawer
              opened={isDrawerOpen}
              onClose={() => setIsDrawerOpen(false)}
              overlayProps={{ opacity: 0, blur: 0 }}
              position="left"
              size="100%"
              withCloseButton={false}
              zIndex={1000}
              styles={{
                body: {
                  padding: 0,
                  height: "100vh",
                  width: "100%",
                },
                content: {
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                },
                header: {
                  padding: 0,
                },
                close: {
                  top: 24,
                  right: 24,
                  position: "absolute",
                },
              }}
            >
              <Drawer.Content>
                <Drawer.CloseButton />
                <Drawer.Body>
                  {tempPassword ? (
                    <TempPassword
                      isLoading={isLoading}
                      setIsLoading={setIsLoading}
                    />
                  ) : createAccount ? (
                    <SignUp isLoading={isLoading} setIsLoading={setIsLoading} />
                  ) : (
                    <SignIn isLoading={isLoading} setIsLoading={setIsLoading} />
                  )}
                </Drawer.Body>
              </Drawer.Content>
            </Drawer>
          )}
        </Flex>
      )}
      {isSpider ? (
        <CloseButton
          title={t("global.modal.close_modal")}
          bg={"transparent"}
          size={32}
          onClick={handleCancelLogin}
          style={{
            position: "absolute",
            borderRadius: "4px",
            top: 24,
            right: 24,
          }}
        />
      ) : null}
    </Flex>
  );
};
export default Authentication;
